import {
    CSSProperties,
    ReactNode,
    useCallback,
    useEffect,
    useRef,
} from 'react'
import { useNavigate } from 'react-router-dom'
import {
    AdvancedMarker,
    Map as GoogleMap,
    MapMouseEvent,
    useMap,
} from '@vis.gl/react-google-maps'

import { MAP_ID } from '../../app/maps/constants'

export const ZOOM_LEVEL = 6
export const DEFAULT_LAT = 49.7437572
export const DEFAULT_LNG = 15.3386383

const MAP_DEFAULT_PIN = '/images/icons/gmap-pin-default.png'
const MAP_HOVER_PIN = '/images/icons/gmap-pin-hover.png'

export interface MapMarker {
    id?: string | number
    lat: number | string
    lng: number | string
    isHovered?: boolean
    href?: string
}

export interface MapProps {
    markers?: MapMarker[] | null
    onMapClick?: (event: MapMouseEvent) => void
    radius?: number
    fitBounds?: boolean
    fitCircle?: boolean
    fillWithEmptyBounds?: boolean
    boundsSize?: number
    isHoverable?: boolean
    maxZoomProps?: number
    options?: google.maps.MapOptions & { center?: google.maps.LatLngLiteral, zoom?: number }
    style?: CSSProperties
    containerElement?: ReactNode
    mapElement?: ReactNode
}

export const parseSingleMarker = (
    location: { latitude: number | string, longitude: number | string },
    id: string | number,
): MapMarker => ({
    id,
    lat: Number(location.latitude),
    lng: Number(location.longitude),
    isHovered: false,
})

const computeBounds = (
    markers: MapMarker[],
    fillWithEmptyBounds: boolean | undefined,
    boundsSize: number,
): google.maps.LatLngBounds => {
    const bounds = new google.maps.LatLngBounds()

    markers.forEach(marker => {
        bounds.extend({
            lat: Number(marker.lat),
            lng: Number(marker.lng),
        })
    })

    const ne = bounds.getNorthEast()
    const sw = bounds.getSouthWest()

    if (ne && sw && ne.equals(sw) && fillWithEmptyBounds !== false) {
        bounds.extend(new google.maps.LatLng(ne.lat() + boundsSize, ne.lng() + boundsSize))
        bounds.extend(new google.maps.LatLng(ne.lat() - boundsSize, ne.lng() - boundsSize))
    }

    return bounds
}

interface MarkerCircleProps {
    center: google.maps.LatLngLiteral
    radiusMeters: number
    onReady?: (circle: google.maps.Circle) => void
}

// Native google.maps.Circle does not have a vis.gl wrapper, so manage imperatively
const MarkerCircle = ({ center, radiusMeters, onReady }: MarkerCircleProps) => {
    const map = useMap()
    const circleRef = useRef<google.maps.Circle | null>(null)

    useEffect(() => {
        if (!map) {
            return
        }

        const circle = new google.maps.Circle({
            map,
            center,
            radius: radiusMeters,
            draggable: false,
            editable: false,
            visible: true,
        })

        circleRef.current = circle
        onReady?.(circle)

        return () => {
            circle.setMap(null)
            circleRef.current = null
        }
    }, [map, center, radiusMeters, onReady])

    return null
}

interface MarkerPinProps {
    marker: MapMarker
    isHoverable: boolean | undefined
    onSelect: (marker: MapMarker) => void
}

const MarkerPin = ({ marker, isHoverable, onSelect }: MarkerPinProps) => {
    const iconUrl = marker.isHovered ? MAP_HOVER_PIN : MAP_DEFAULT_PIN

    const handlePointerEnter = isHoverable ? () => { marker.isHovered = true } : undefined
    const handlePointerLeave = isHoverable ? () => { marker.isHovered = false } : undefined

    return (
        <AdvancedMarker
            position={{
                lat: Number(marker.lat),
                lng: Number(marker.lng),
            }}
            zIndex={marker.isHovered ? 10 : 0}
            onClick={() => onSelect(marker)}
        >
            <img
                src={iconUrl}
                alt=""
                width={32}
                height={42}
                onPointerEnter={handlePointerEnter}
                onPointerLeave={handlePointerLeave}
            />
        </AdvancedMarker>
    )
}

interface MapControllerProps {
    markers: MapMarker[] | null | undefined
    fitBounds: boolean | undefined
    fillWithEmptyBounds: boolean | undefined
    radius: number | undefined
    fitCircle: boolean | undefined
    boundsSize: number | undefined
    circleRef: { current: google.maps.Circle | null }
}

const MapController = ({
    markers,
    fitBounds,
    fillWithEmptyBounds,
    radius,
    fitCircle,
    boundsSize,
    circleRef,
}: MapControllerProps) => {
    const map = useMap()

    useEffect(() => {
        if (!map || !markers || markers.length === 0) {
            return
        }

        if (fitBounds !== false) {
            map.fitBounds(computeBounds(markers, fillWithEmptyBounds, boundsSize ?? 0.02))

            return
        }

        if (radius && fitCircle === true && circleRef.current) {
            const circleBounds = circleRef.current.getBounds()

            if (circleBounds) {
                map.fitBounds(circleBounds)
            }
        }
    }, [map, markers, fitBounds, fillWithEmptyBounds, boundsSize, radius, fitCircle, circleRef])

    return null
}

const Map = ({
    markers,
    onMapClick,
    radius,
    fitBounds,
    fitCircle,
    fillWithEmptyBounds,
    boundsSize,
    isHoverable,
    maxZoomProps: _maxZoomProps,
    options,
    style,
    containerElement,
    mapElement,
}: MapProps) => {
    const navigate = useNavigate()
    const circleRef = useRef<google.maps.Circle | null>(null)

    const handleMarkerClick = useCallback((marker: MapMarker) => {
        if (marker.href) {
            navigate(marker.href)
        }
    }, [navigate])

    const containerStyle: CSSProperties = {
        width: '100%',
        height: '100%',
        minHeight: 200,
        ...style,
    }

    const {
        center: _ignoredCenter,
        zoom: _ignoredZoom,
        ...restOptions
    } = options ?? {}

    const initialCenter: google.maps.LatLngLiteral = options?.center ?? {
        lat: DEFAULT_LAT,
        lng: DEFAULT_LNG,
    }
    const initialZoom = options?.zoom ?? ZOOM_LEVEL
    // containerElement/mapElement are legacy from react-google-maps recompose HOCs;
    // accepted for backwards compatibility but no longer needed with @vis.gl APIProvider.
    void containerElement
    void mapElement
    void _ignoredCenter
    void _ignoredZoom

    return (
        <div className="map-element" style={containerStyle}>
            <GoogleMap
                mapId={MAP_ID}
                defaultCenter={initialCenter}
                defaultZoom={initialZoom}
                scrollwheel={restOptions.scrollwheel ?? false}
                gestureHandling={restOptions.scrollwheel ? 'greedy' : 'cooperative'}
                zoomControl
                mapTypeControl={restOptions.mapTypeControl ?? false}
                fullscreenControl={restOptions.fullscreenControl ?? true}
                streetViewControl={restOptions.streetViewControl ?? false}
                style={{ width: '100%', height: '100%', minHeight: 200 }}
                onClick={event => onMapClick?.(event)}
            >
                {markers?.map((marker, index) => (
                    <MarkerPin
                        key={`marker-${marker.id ?? index}`}
                        marker={marker}
                        isHoverable={isHoverable}
                        onSelect={handleMarkerClick}
                    />
                ))}
                {radius && markers?.map((marker, index) => (
                    <MarkerCircle
                        key={`circle-${marker.id ?? index}`}
                        center={{ lat: Number(marker.lat), lng: Number(marker.lng) }}
                        radiusMeters={radius * 1000}
                        onReady={circle => { circleRef.current = circle }}
                    />
                ))}
                <MapController
                    markers={markers}
                    fitBounds={fitBounds}
                    fillWithEmptyBounds={fillWithEmptyBounds}
                    radius={radius}
                    fitCircle={fitCircle}
                    boundsSize={boundsSize}
                    circleRef={circleRef}
                />
            </GoogleMap>
        </div>
    )
}

export default Map
